<template>
  <v-card flat>
    <v-card-text>
      <!-- Summary Cards -->
      <v-row v-if="report" class="mb-4">
        <v-col cols="6" sm="4">
          <v-card color="red darken-2" dark class="pa-3 text-center" flat>
            <div class="text-h5 font-weight-bold">{{ report.total_count }}</div>
            <div class="caption">Out of Stock Items</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="4">
          <v-card color="orange darken-1" dark class="pa-3 text-center" flat>
            <div class="text-h5 font-weight-bold">{{ criticalCount }}</div>
            <div class="caption">Out 30+ Days</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="4">
          <v-card color="blue darken-1" dark class="pa-3 text-center" flat>
            <div class="text-h6 font-weight-bold">
              {{ report.generated_at }}
            </div>
            <div class="caption">Generated At</div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Data Table -->
      <v-data-table
        v-if="report"
        :items="report.items"
        :headers="headers"
        :search="search"
        item-key="id"
        dense
        :items-per-page="25"
        :footer-props="{ 'items-per-page-options': [10, 25, 50, -1] }"
      >
        <template v-slot:top>
          <v-text-field
            v-model="search"
            prepend-icon="mdi-magnify"
            label="Search items..."
            dense
            class="mb-2"
          />
        </template>
        <template v-slot:item.quantity="{ item }">
          <v-chip color="red" dark small>{{ item.quantity }}</v-chip>
        </template>
        <template v-slot:item.days_out_of_stock="{ item }">
          <v-chip
            :color="getDaysColor(item.days_out_of_stock)"
            dark
            small
            v-if="item.days_out_of_stock !== null"
          >
            {{ item.days_out_of_stock }} days
          </v-chip>
          <span v-else class="grey--text">N/A</span>
        </template>
        <template v-slot:item.unit_price="{ item }">
          {{ formatCurrency(item.unit_price) }}
        </template>
        <template v-slot:item.selling_price="{ item }">
          {{ formatCurrency(item.selling_price) }}
        </template>
        <template v-slot:item.batch_name="{ item }">
          {{ item.batch_name || "-" }}
        </template>
        <template v-slot:item.branch="{ item }">
          {{ item.branch ? item.branch.branch_name : "-" }}
        </template>
      </v-data-table>

      <!-- Empty State -->
      <div v-if="!report" class="text-center grey--text pa-10">
        <v-icon size="64" color="grey lighten-1"
          >mdi-package-variant-remove</v-icon
        >
        <p class="mt-4 subtitle-1">
          Click "Generate" to view items that are completely out of stock.
        </p>
      </div>

      <!-- No Results -->
      <div
        v-if="report && report.items && report.items.length === 0"
        class="text-center pa-10"
      >
        <v-icon size="64" color="green">mdi-check-circle</v-icon>
        <p class="mt-4 subtitle-1 green--text">
          All items have stock available. No items are out of stock.
        </p>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapActions } from "vuex";
export default {
  name: "OutOfStockReport",
  props: {
    filters: { type: Object, required: true },
  },
  data() {
    return {
      report: null,
      search: "",
      headers: [
        { text: "Item Name", value: "item_name" },
        { text: "Type", value: "item_type" },
        { text: "Supplier", value: "supplier" },
        { text: "Batch", value: "batch_name" },
        { text: "Current Qty", value: "quantity", align: "center" },
        { text: "Last Sold", value: "last_sold_date" },
        {
          text: "Days Out of Stock",
          value: "days_out_of_stock",
          align: "center",
        },
        { text: "Reorder Point", value: "reorder_point", align: "center" },
        { text: "Unit Cost", value: "unit_price", align: "end" },
        { text: "Selling Price", value: "selling_price", align: "end" },
        { text: "Branch", value: "branch" },
      ],
    };
  },
  computed: {
    criticalCount() {
      if (!this.report || !this.report.items) return 0;
      return this.report.items.filter(
        (i) => i.days_out_of_stock !== null && i.days_out_of_stock >= 30
      ).length;
    },
  },
  methods: {
    ...mapActions({
      fetchReport: "inventory_reports/get_out_of_stock_report",
    }),
    formatCurrency(value) {
      if (!value && value !== 0) return "0.00";
      return Number(value).toLocaleString("en-PH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    getDaysColor(days) {
      if (days === null) return "grey";
      if (days >= 60) return "red darken-3";
      if (days >= 30) return "red";
      if (days >= 14) return "orange";
      return "amber darken-1";
    },
    async generate() {
      try {
        let params = {};
        if (this.filters.item_type_id)
          params.item_type_id = this.filters.item_type_id;
        if (this.filters.supplier) params.supplier = this.filters.supplier;
        if (this.filters.branch_id) params.branch_id = this.filters.branch_id;
        this.report = await this.fetchReport(params);
      } catch (e) {
        this.$alert({
          title: "Error",
          message:
            "Error generating out of stock report: " +
            (e.response?.data?.error || e.message),
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
    },
  },
};
</script>
